import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ErpTable, EmptyRow, Panel, SectionHeader, Td, Th } from '@/components/erp/primitives'
import { ErrorStrip, Forbidden, LoadingBlock, Note } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { explain } from '@/shared/api/problem'
import { fmtDateTime } from '@/shared/lib/format'
import { exportStatus, listExports, listPolls, requestExport } from './api'
import { PollSelect } from './PollSelect'
import { Pill, UNIMPLEMENTED_FORMATS, count, exportFormatLabel } from './vocab'

const FORMATS = ['CSV', 'VERBATIM_CSV', 'CODEBOOK_CSV', 'SPSS_SAV', 'STATA_DTA']
const PII_MODES = [
  { code: 'NONE', label: 'No respondent columns' },
  { code: 'MASKED', label: 'Masked (initials and a phone tail)' },
  { code: 'FULL', label: 'Identifiers — needs a written reason' },
]

/**
 * Datasets, codebooks and verbatims.
 *
 * ## Two formats are offered and refused, on purpose
 *
 * SPSS and Stata are in the picker. The server declares them and answers with a
 * sentence explaining that writing either correctly needs a binary-format library
 * that is not on the classpath, and pointing at CSV plus the codebook — which
 * every statistics package reads. A format silently missing from a dropdown is
 * one somebody assumes is coming.
 *
 * PollSphere advertises six formats and implements none: its "Generate Export"
 * opens a paginated JSON envelope — respondent names, phones, IPs and
 * fingerprints included — in a browser tab.
 *
 * ## FULL asks for the reason before it asks the server
 *
 * Not because the client is the control — the server refuses without one — but
 * because discovering the requirement AFTER pressing a button is how somebody
 * types "asdf" into it.
 */
export function ExportsPage() {
  const canExport = useCan('survey:export')
  const canExportPii = useCan('survey:export-pii')
  const [params, setParams] = useSearchParams()
  const pollId = params.get('poll')
  const [format, setFormat] = useState('CSV')
  const [piiMode, setPiiMode] = useState('MASKED')
  const [includeFlagged, setIncludeFlagged] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fresh, setFresh] = useState<string | null>(null)

  const polls = useLoad(() => listPolls({}, 0, 100), [])
  const jobs = useLoad(() => (pollId ? listExports(pollId) : Promise.resolve([])), [pollId])

  if (!canExport) return <Forbidden what="exports" />

  const setPoll = (id: string) => {
    const next = new URLSearchParams(params)
    if (id) next.set('poll', id)
    else next.delete('poll')
    setParams(next, { replace: true })
    setFresh(null)
  }

  async function generate() {
    if (!pollId) return
    let justification: string | undefined
    if (piiMode === 'FULL') {
      const typed = window.prompt(
        'Why does this export need respondent identifiers? It is stored on the job and written '
        + 'to the audit log with the poll and the row count.')
      if (!typed || !typed.trim()) return
      justification = typed.trim()
    }
    setBusy(true)
    setError(null)
    try {
      const job = await requestExport(pollId, {
        formatCode: format, piiModeCode: piiMode, includeFlagged, justification,
      })
      setFresh(job.downloadUrl)
      jobs.reload()
    } catch (e) {
      setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  /** The first presign expires; this mints another rather than regenerating the file. */
  async function refresh(jobId: string) {
    setError(null)
    try {
      const job = await exportStatus(jobId)
      if (job.downloadUrl) window.open(job.downloadUrl, '_blank', 'noopener')
      else setError('That export has expired and its file has been purged.')
    } catch (e) {
      setError(explain(e))
    }
  }

  const rows = jobs.data ?? []

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Exports"
        description="Every dataset ships with its codebook, and its header block names the weight run."
        right={<PollSelect polls={polls.data?.content ?? []} value={pollId} onChange={setPoll} />}
      />

      <ErrorStrip message={error ?? jobs.error} />

      {!pollId ? (
        <Note tone="info">Pick a poll.</Note>
      ) : (
        <>
          <Panel title="Generate">
            <div className="flex flex-wrap items-end gap-3">
              <label className="flex flex-col gap-1.5 text-erp-xs">
                Format
                <select className="erp-input h-9 min-w-[200px]" value={format}
                  onChange={(e) => setFormat(e.target.value)}>
                  {FORMATS.map((f) => (
                    <option key={f} value={f}>
                      {exportFormatLabel(f)}{UNIMPLEMENTED_FORMATS.has(f) ? ' — not produced' : ''}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1.5 text-erp-xs">
                Respondent columns
                <select className="erp-input h-9 min-w-[260px]" value={piiMode}
                  onChange={(e) => setPiiMode(e.target.value)}>
                  {PII_MODES.map((m) => (
                    <option key={m.code} value={m.code}
                      disabled={m.code === 'FULL' && !canExportPii}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-1.5 text-erp-xs h-9">
                <input type="checkbox" checked={includeFlagged}
                  onChange={(e) => setIncludeFlagged(e.target.checked)} />
                Include flagged interviews
              </label>
              <button type="button" className="erp-btn-primary h-9 text-erp-sm"
                disabled={busy} onClick={generate}>
                {busy ? 'Generating…' : 'Generate'}
              </button>
            </div>

            {UNIMPLEMENTED_FORMATS.has(format) && (
              <div className="mt-3">
                <Note tone="info">
                  This build does not produce {exportFormatLabel(format)}. Pressing Generate will
                  return the server's explanation, which points at CSV plus the codebook —
                  together they carry the variable names and value labels a .sav or .dta would
                  have held.
                </Note>
              </div>
            )}

            {piiMode === 'FULL' && (
              <div className="mt-3">
                <Note tone="bad">
                  An export is the moment a respondent list leaves the building, and no
                  permission in this system applies to it afterwards. This one needs a written
                  reason and is audited with the poll and the row count.
                </Note>
              </div>
            )}

            {fresh && (
              <div className="mt-3 flex items-center gap-2">
                <a className="erp-btn-secondary h-9 text-erp-sm" href={fresh}
                  target="_blank" rel="noopener noreferrer">
                  Download
                </a>
                <span className="text-erp-xs text-muted-foreground">
                  Short-lived link. The job below mints a fresh one when it expires.
                </span>
              </div>
            )}
          </Panel>

          {jobs.loading && rows.length === 0 ? <LoadingBlock label="Loading exports…" /> : (
            <Panel title="Recent exports" description="The row is the record of what left the building.">
              <ErpTable head={(
                <>
                  <Th>When</Th>
                  <Th>Format</Th>
                  <Th>Respondents</Th>
                  <Th>State</Th>
                  <Th className="text-right">Rows</Th>
                  <Th className="text-right">Download</Th>
                </>
              )}>
                {rows.length === 0 ? (
                  <EmptyRow colSpan={6} message="Nothing has been exported from this poll." />
                ) : rows.map((job) => (
                  <tr key={job.id} className="hover:bg-muted/40">
                    <Td className="text-muted-foreground">{fmtDateTime(job.createdAt)}</Td>
                    <Td>{exportFormatLabel(job.formatCode)}</Td>
                    <Td>
                      <Pill tone={job.piiModeCode === 'FULL'
                        ? 'bg-rose-500/10 text-rose-600 border-rose-500/30' : undefined}>
                        {job.piiModeCode}
                      </Pill>
                    </Td>
                    <Td>
                      <Pill>{job.stateCode}</Pill>
                      {job.truncated && (
                        <span className="ml-1.5 text-erp-xs text-amber-600">truncated</span>
                      )}
                    </Td>
                    <Td className="text-right tabular-nums">{count(job.rowCount)}</Td>
                    <Td className="text-right">
                      {job.stateCode === 'COMPLETED' ? (
                        <button type="button"
                          className="erp-btn-ghost h-8 text-erp-xs text-chocolate"
                          onClick={() => refresh(job.id)}>
                          Fresh link
                        </button>
                      ) : (
                        <span className="text-erp-xs text-muted-foreground">
                          {job.errorCode ?? '—'}
                        </span>
                      )}
                    </Td>
                  </tr>
                ))}
              </ErpTable>
            </Panel>
          )}
        </>
      )}
    </div>
  )
}
